import './WebsitesTemplates.scss';

import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import {
  AspectRatio,
  Carousel,
  CarouselSlide,
  Column,
  FieldSet,
  FieldSetLegend,
  GridContainer,
  H2,
  Hidden,
  P,
  Pagination,
  PaginationButton,
  PaginationStep,
  Row,
  SelectionSet,
  SelectionSetInput,
  SelectionSetLabel,
  Visible,
} from '@vp/swan';

import {
  FeatureGroupModel,
  MediaLinkModel,
} from '@vp/digital-common-data-model-lib';

import FluidImageLoading from './FluidImageLoading';
import MissingContentAlert from '../../common/MissingContentAlert';
import { trackTemplateSectionSelected } from '../WixWebsitesAnalytics';

type TemplateProps = {
  content?: FeatureGroupModel;
  accessibility: FeatureGroupModel;
};

type ColumnSpan = NonNullable<2 | 1 | 8 | 4 | 3 | 5 | 6 | 7 | 9 | 10 | 11 | 12>;

interface templateColumnModel {
  span: ColumnSpan;
  width: number;
  vw: number;
  aspectRatio: number;
  image: MediaLinkModel;
}

const ITEMS_PER_PAGE = 8;
const CAROUSEL_MAX_ITEMS = 10;
const MOST_POPULAR_KEY = 'mostPopular';

const DESKTOP_VW = 33;
const MOBILE_VW = 16;

const DESKTOP_WIDTH = 1772;
const Mobile_WIDTH = 886;

const WebsitesTemplates = ({
  content,
  accessibility,
}: TemplateProps): JSX.Element => {
  const [selectedSection, setSelectedSection] = useState(MOST_POPULAR_KEY);
  const [carouselAriaLabel, setCarouselAriaLabel] = useState(
    content?.features[0]?.altText ?? ''
  );
  const [imageAltText, setImageAltText] = useState(
    content?.features[0]?.hoverText ?? ''
  );
  if (!content) {
    return <MissingContentAlert />;
  }

  const templates = content.media.filter(
    (media) => media.key === selectedSection
  );

  const desktopTemplates = templates.map((templates) => templates.icons).flat();

  const mobileTemplates = templates
    .map((templates) => templates.iconsMobile)
    .flat();

  const numberOfPages = Math.ceil(
    (desktopTemplates.length + mobileTemplates.length) / ITEMS_PER_PAGE
  );

  return (
    <>
      <GridContainer>
        <Row>
          <Column span={12}>
            <H2 fontSize="x2large">{content.title}</H2>

            <P mb={0}>{content.description}</P>
          </Column>
        </Row>

        <Row mt={7}>
          <Column span={12}>
            <FieldSet className="wix-websites-pdp-e__templates__categories">
              <FieldSetLegend visuallyHidden>
                {accessibility.features[10].title}
              </FieldSetLegend>

              <SelectionSet
                variant="single-select"
                skin="buttons"
                defaultSelectedValue={selectedSection}
                className="wix-websites-pdp-e__templates__selection"
              >
                {content.features.map((feature) => (
                  <SelectionSetInput
                    key={feature.key}
                    value={feature.key}
                    onClick={() => {
                      setSelectedSection(feature.key);
                      setCarouselAriaLabel(feature.altText);
                      setImageAltText(feature.hoverText);
                      trackTemplateSectionSelected(feature.key);
                    }}
                  >
                    <SelectionSetLabel mb={0}>
                      {feature.title}
                    </SelectionSetLabel>
                  </SelectionSetInput>
                ))}
              </SelectionSet>
            </FieldSet>
          </Column>
        </Row>
      </GridContainer>

      <Visible xs>
        <GridContainer>
          <TemplatesMobile
            desktopTemplates={desktopTemplates.slice(0, CAROUSEL_MAX_ITEMS)}
            accessibility={accessibility}
            carouselAriaLabel={carouselAriaLabel}
            imageAltText={imageAltText}
          />
        </GridContainer>
      </Visible>

      <Hidden xs>
        <GridContainer>
          <TemplatesDesktop
            selectedSection={selectedSection}
            desktopTemplates={desktopTemplates}
            mobileTemplates={mobileTemplates}
            numberOfPages={numberOfPages}
            accessibility={accessibility}
            imageAltText={imageAltText}
          />
        </GridContainer>
      </Hidden>
    </>
  );
};

type TemplatesDesktopProps = {
  selectedSection: string;
  desktopTemplates: any[];
  mobileTemplates: any[];
  numberOfPages: number;
  accessibility: FeatureGroupModel;
  imageAltText: string;
};

const TemplatesDesktop = ({
  selectedSection,
  desktopTemplates,
  mobileTemplates,
  numberOfPages,
  accessibility,
  imageAltText,
}: TemplatesDesktopProps): JSX.Element => {
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    if (currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [selectedSection]);

  const handlePageChange = (page: number) => {
    if (page < 0) {
      page = 0;
    }
    setCurrentPage(page % numberOfPages);
  };

  const indexFirstItem = (ITEMS_PER_PAGE / 2) * currentPage;
  const templatesPerPage: Array<templateColumnModel> = [
    {
      span: 4,
      width: DESKTOP_WIDTH,
      vw: DESKTOP_VW,
      aspectRatio: 0.72,
      image: desktopTemplates[indexFirstItem],
    },
    {
      span: 2,
      width: Mobile_WIDTH,
      vw: MOBILE_VW,
      aspectRatio: 1.43,
      image: mobileTemplates[indexFirstItem],
    },
    {
      span: 4,
      width: DESKTOP_WIDTH,
      vw: DESKTOP_VW,
      aspectRatio: 0.72,
      image: desktopTemplates[indexFirstItem + 1],
    },
    {
      span: 2,
      width: Mobile_WIDTH,
      vw: MOBILE_VW,
      aspectRatio: 1.43,
      image: mobileTemplates[indexFirstItem + 1],
    },
    {
      span: 2,
      width: Mobile_WIDTH,
      vw: MOBILE_VW,
      aspectRatio: 1.43,
      image: mobileTemplates[indexFirstItem + 2],
    },
    {
      span: 4,
      width: DESKTOP_WIDTH,
      vw: DESKTOP_VW,
      aspectRatio: 0.72,
      image: desktopTemplates[indexFirstItem + 2],
    },
    {
      span: 2,
      width: Mobile_WIDTH,
      vw: MOBILE_VW,
      aspectRatio: 1.43,
      image: mobileTemplates[indexFirstItem + 3],
    },
    {
      span: 4,
      width: DESKTOP_WIDTH,
      vw: DESKTOP_VW,
      aspectRatio: 0.72,
      image: desktopTemplates[indexFirstItem + 3],
    },
  ];

  return (
    <>
      <Row className="wix-websites-pdp-e__templates-gallery" mt={7}>
        {templatesPerPage.map(
          (template: templateColumnModel, index: number) => (
            <Column span={template.span} key={index}>
              <AspectRatio
                ratio={0.71}
                className={classNames(
                  'wix-websites-pdp-e__templates__image-container',
                  { empty: !template.image }
                )}
              >
                {template.image && (
                  <FluidImageLoading
                    media={template.image}
                    sizes={`
                    (max-width: 768px) 100vw, 
                    (max-width: 5316px) ${template.vw}vw, 
                    ${template.width}px
                    `}
                    useLoader={false}
                    alt={imageAltText.replace('{{number}}', String(index + 1))}
                  />
                )}
              </AspectRatio>
            </Column>
          )
        )}
      </Row>

      <Row mt={7}>
        <Column span={12} display={'flex'} style={{ justifyContent: 'center' }}>
          <Pagination accessibleText={accessibility.features[6].title}>
            <PaginationButton
              variant="previous"
              component="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
              accessibleText={accessibility.features[7].title}
            />

            {Array.from({ length: numberOfPages }, (_, index) => (
              <PaginationStep
                key={index}
                component="button"
                active={index === currentPage}
                onClick={() => handlePageChange(index)}
                accessibleText={accessibility.features[9].title.replace(
                  '{{PAGE_NUMBER}}',
                  String(index + 1)
                )}
              >
                {index + 1}
              </PaginationStep>
            ))}

            <PaginationButton
              variant="next"
              component="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === numberOfPages - 1}
              accessibleText={accessibility.features[8].title}
            />
          </Pagination>
        </Column>
      </Row>
    </>
  );
};

type TemplatesMobileProps = {
  desktopTemplates: any[];
  accessibility: FeatureGroupModel;
  carouselAriaLabel: string;
  imageAltText: string;
};

const TemplatesMobile = ({
  desktopTemplates,
  accessibility,
  carouselAriaLabel,
  imageAltText,
}: TemplatesMobileProps): JSX.Element => (
  <Row mt={7}>
    <Column span={12} px={3}>
      <Carousel
        slidesToShow={1.15}
        currentSlide={0}
        infinite={true}
        gridGutters
        dotsClass="slick-dots templates__dots-wrapper"
        className="templates__slider-wrapper"
        progressIndicator="dots"
        arrows={false}
        accessibleTextForDots={
          desktopTemplates
            ? desktopTemplates.map(
                (feature, index) =>
                  accessibility.features[5]?.title.replace(
                    '{{SLIDE_NUMBER}}',
                    String(index + 1)
                  ) ?? ''
              )
            : []
        }
        accessibleTextForVisibleSlides={accessibility.features[2]?.valueText}
        accessibleTextForSlide={accessibility.features[2]?.altText}
        accessibleTextForNext={accessibility.features[4].title}
        accessibleTextForPrevious={accessibility.features[3].title}
        aria-roledescription={accessibility.features[0].title}
        aria-label={carouselAriaLabel}
      >
        {desktopTemplates?.map(
          (template: any, index: number) =>
            template?.secureUrl && (
              <CarouselSlide
                aria-roledescription={accessibility.features[1].title}
                key={index}
                className="wix-websites-pdp-e__templates__image-container wix-websites-pdp__slide"
              >
                <FluidImageLoading
                  media={template}
                  sizes={`
                      (max-width: 768px) 100vw, 
                      ${DESKTOP_WIDTH}px
                    `}
                  useLoader={false}
                  alt={imageAltText.replace('{{number}}', String(index + 1))}
                />
              </CarouselSlide>
            )
        )}
      </Carousel>
    </Column>
  </Row>
);

export default WebsitesTemplates;
